import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, Container, Alert, LinearProgress, Backdrop } from '@mui/material';
import {
  Edit as EditIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';

import { usePropertyDetailForm } from '../../hooks/use-property-detail-form';
import PropertiesDetailFormMobile from '../mobile/properties-detail-form-mobile';
import { getInitialReceiptDocuments } from '../../api/get-properties';

import BasicSectionCommon from './basic-section-common';
import { NavigationPanel } from './navigation-panel';
import ReceivedMaterialsList from './received-materials-list';

import type { PropertyDetailFormProps } from '../../types/property.types';
import type { ReceiptDocumentItem } from '../../types/property.types';

import { useIsMobile } from '@/hooks/use-is-mobile';
import { useBeforeUnloadWarning } from '@/hooks/use-beforeunload-warning';
import { useSubmitting } from '@/hooks/use-submitting';
import HeaderRegister from '@/features/apps/components/header-register';
import { paths } from '@/config/paths';
import { ActionButton, FixedFooter } from '@/components/footer/fixed-footer';
import { useSnackbar } from '@/hooks/use-snackbar';
import { MESSAGES } from '@/lib/constants';
import { snackbarType } from '@/lib/enums';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useLoadingProgress } from '@/hooks/use-loading-progress';

export const PropertiesDetailForm: React.FC<PropertyDetailFormProps> = ({
  onCancel,
  onDelete,
  onArchive,
}) => {
  const navigate = useNavigate();

  const {
    // Data
    formData,
    detailFormData,
    originalFormData,
    // State
    isLoading,
    hasFormChanged,

    // Form actions
    updateField,
    addFieldStaff,
    updateFieldStaff,
    removeFieldStaff,
    addContractFactory,
    updateContractFactory,
    removeContractFactory,
    handleSubmit,
    resetForm,
    handleDepartmentChange,

    // Validation
    getFieldError,
    isFieldValid,
    validationErrors,

    // Departments and Staffs
    departments,
    departmentsLoading,
    salesStaffs,
    deliveryStaffs,
    staffsLoading,

    // Companies
    customers,
    constructionCompanies,
    companiesLoading,

    // Computed properties
    isSubmitDisabled,
    canUpdateProperty,
    canManagePrice,
    salesStaffSelected,
    deliveryStaffSelected,
    departmentSelected,

    // Document upload tracking
    onDocumentsUploaded,
  } = usePropertyDetailForm();

  // Show popup warning when user tries to close/refresh page with unsaved changes
  useBeforeUnloadWarning(hasFormChanged);
  const isMobile = useIsMobile();
  const isArchive = !!formData?.is_archive;

  const { setSnackbarMessage } = useSnackbar();
  const { setConfirmDialog } = useConfirmDialog();
  const { setIsLoading } = useLoadingProgress();
  const { setIsSubmitting } = useSubmitting();
  const { propertyId } = useParams<{ propertyId: string }>();

  // Manage receipt documents state for ReceivedMaterialsList (outside inert box)
  const [initialReceiptDocuments, setInitialReceiptDocuments] = useState<ReceiptDocumentItem[]>([]);

  useEffect(() => {
    if (initialReceiptDocuments.length === 0 && formData.receipt_documents) {
      setInitialReceiptDocuments(formData.receipt_documents);
    }
  }, [formData.receipt_documents]);

  const handleFetchInitialReceiptDocuments = useCallback(async () => {
    if (!propertyId) {
      return;
    }
    const data = await getInitialReceiptDocuments(propertyId);
    setInitialReceiptDocuments(data);
  }, [propertyId]);
  const handleFormSubmit = useCallback(async () => {
    if (handleSubmit) {
      setIsLoading(true);
      const success = await handleSubmit(); // Already waits for API response

      if (success) {
        setIsSubmitting(true);
        setSnackbarMessage(MESSAGES.EDIT_PROPERTY_SUCCESS, { type: snackbarType.SUCCESS });
        navigate(paths.properties.path);
      } else {
        setConfirmDialog({
          title: '更新に失敗しました。もう一度お試しください。',
        });
      }
      setIsLoading(false);
    }
  }, [handleSubmit, navigate]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [resetForm, onCancel]);

  const handleDelete = useCallback(async () => {
    if (onDelete) {
      await onDelete();
    }
  }, [onDelete]);

  const handleArchive = useCallback(async () => {
    if (onArchive) {
      try {
        await onArchive(formData?.is_archive ?? false);
      } catch (error) {
        console.error('Error archiving property:', error);
      }
    }
  }, [onArchive, formData?.is_archive]);

  const footerButtons: ActionButton[] = [
    {
      label: '削除',
      onClick: handleDelete,
      variant: 'outlined' as const,
      startIcon: <DeleteIcon />,
      disabled: isLoading || !canUpdateProperty,
      sx: {
        color: 'red',
        borderColor: 'red',
        '&:hover': {
          borderColor: 'darkred',
          backgroundColor: 'rgba(255, 0, 0, 0.04)',
        },
      },
    },
    !isArchive
      ? {
          label: 'アーカイブ',
          onClick: handleArchive,
          variant: 'outlined' as const,
          startIcon: <ArchiveIcon />,
          disabled: isLoading || !canUpdateProperty,
        }
      : {
          label: 'アクティブに戻す',
          onClick: handleArchive,
          variant: 'outlined' as const,
          startIcon: <UnarchiveIcon />,
          disabled: isLoading || !canUpdateProperty,
        },
    {
      label: 'キャンセル',
      onClick: handleCancel,
      variant: 'outlined' as const,
      startIcon: <CancelIcon />,
      disabled: isLoading,
      sx: {
        marginLeft: 'auto',
      },
    },
    {
      label: '入力内容を決定',
      onClick: handleFormSubmit,
      variant: 'contained' as const,
      startIcon: <EditIcon />,
      disabled: isSubmitDisabled || !canUpdateProperty,
      loading: isLoading,
    },
  ].filter(Boolean);
  if (isMobile) {
    return (
      <PropertiesDetailFormMobile
        detailFormData={detailFormData}
        customers={customers}
        constructionCompanies={constructionCompanies}
      />
    );
  }
  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <HeaderRegister title='現場詳細' description='現場情報の詳細表示・編集' />

      {/* Loading Indicator */}
      {isLoading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* General Error Messages */}
      {validationErrors.some((err) => err.field === 'general') && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {validationErrors.find((err) => err.field === 'general')?.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Form Content */}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {/* Form sections with inert when view-only */}
          <Box sx={{ position: 'relative' }} inert={!canUpdateProperty ? true : false}>
            <BasicSectionCommon
              formData={formData}
              updateField={updateField}
              getFieldError={getFieldError}
              isFieldValid={isFieldValid}
              addFieldStaff={addFieldStaff}
              updateFieldStaff={updateFieldStaff}
              removeFieldStaff={removeFieldStaff}
              addContractFactory={addContractFactory}
              updateContractFactory={updateContractFactory}
              removeContractFactory={removeContractFactory}
              handleDepartmentChange={handleDepartmentChange}
              departments={departments}
              departmentSelected={departmentSelected}
              departmentsLoading={departmentsLoading}
              salesStaffs={salesStaffs}
              salesStaffSelected={salesStaffSelected}
              deliveryStaffs={deliveryStaffs}
              deliveryStaffSelected={deliveryStaffSelected}
              staffsLoading={staffsLoading}
              customers={customers}
              constructionCompanies={constructionCompanies}
              companiesLoading={companiesLoading}
              canManagePrice={canManagePrice}
              onDocumentsUploaded={onDocumentsUploaded}
              originalFormData={originalFormData ?? undefined}
              initialReceiptDocuments={initialReceiptDocuments}
              handleFetchInitialReceiptDocuments={handleFetchInitialReceiptDocuments}
            />

            <Backdrop
              open={!canUpdateProperty}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0)',
                zIndex: 9999,
              }}
            ></Backdrop>
          </Box>

          {/* Received Materials List - Outside inert box to allow download even when view-only */}
          {propertyId && (
            <Box id='received-materials-list-section' sx={{ scrollMarginTop: '62px', mt: 3 }}>
              <ReceivedMaterialsList
                files={initialReceiptDocuments}
                handleFetchInitialReceiptDocuments={handleFetchInitialReceiptDocuments}
                canUpdateProperty={canUpdateProperty}
                onDocumentsUploaded={onDocumentsUploaded}
              />
            </Box>
          )}

          {/* Add bottom padding to prevent content from being hidden behind fixed footer */}
          <Box sx={{ height: '100px' }} />
        </Box>
        <FixedFooter buttons={footerButtons} isSubmitting={isLoading} />

        {/* Navigation Panel */}
        <Box sx={{ width: 250, flexShrink: 0 }}>
          <NavigationPanel isDetailPage={true} />
        </Box>
      </Box>
    </Container>
  );
};
